import { Search } from "lucide-react";
import type React from "react";
import { useEffect, useState } from "react";
import { Link, useNavigate, useSearchParams } from "react-router-dom";
import api from "../../lib/api";
import { useAuth } from "../../lib/authContext";
import type {
  OrderStats,
  PharmacyStats,
  PharmacyStatsResponse,
  StatusStats,
} from "../../lib/types";
import { Filters } from "../ui/Filters";
import { StatusCard } from "../ui/StatusCard";
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from "../ui/tooltip";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "../ui/table";
import {
  opsStatus,
  ORDER_START_DATE,
  trackOrderStatus,
} from "../../lib/constants";
import {
  CommandEmpty,
  CommandGroup,
  CommandInput,
  CommandItem,
  CommandList,
} from "../ui/command";
import { CommandDialog } from "../ui/command";
import { COMMANDS, PHARMACY_COMMANDS, generateDynamicPharmacyCommands } from "../../services/mockDataService";
import type { Entity } from "../../lib/types";

export const Dashboard: React.FC = () => {
  const { user, accessLevel, userEntities, userRole } = useAuth();
  const [orders, setOrders] = useState<OrderStats[]>([]);
  const [pharmacyStats, setPharmacyStats] = useState<PharmacyStats[]>([]);
  const [startDate, setStartDate] = useState<string>("");
  const [endDate, setEndDate] = useState<string>("");
  const [selectedState, setSelectedState] = useState<string>("all");
  const [selectedProvider, setSelectedProvider] = useState<number>(0);
  const [selectedMode, setSelectedMode] = useState<string>("all");
  const [selectedEntity, setSelectedEntity] = useState<number>(0);
  const [searchParams, setSearchParams] = useSearchParams();
  const navigate = useNavigate();
  const [isCommandOpen, setIsCommandOpen] = useState(false);
  const [entities, setEntities] = useState<Entity[]>([]);
  const [dynamicPharmacyCommands, setDynamicPharmacyCommands] = useState<typeof PHARMACY_COMMANDS>([]);

  const selectedStatus = searchParams.get("status");
  const [statusFilter, setStatusFilter] = useState<string>(
    selectedStatus ?? "",
  );
  const [currentPage, setCurrentPage] = useState<number>(1);
  const [stateWiseStats, setStateWiseStats] = useState<any[]>([]);

  useEffect(() => {
    const fetchData = async () => {
      const isSuperAdmin = userRole?.role_name?.toLowerCase() === "super admin";
      const hasProviderAccess = accessLevel?.access_level?.provider_id && accessLevel.access_level.provider_id.length > 0;

      const allowedEntityIds = hasProviderAccess
        ? []
        : userEntities && userEntities.length > 0
          ? userEntities.map((e: any) => e.entity_id)
          : accessLevel?.entities?.map((id: string | number) => Number(id)) || [];

      const entitiesParam = allowedEntityIds.length > 0 && allowedEntityIds.length < 500
        ? allowedEntityIds.join(",")
        : undefined;

      const [orders_data, pharmacy_stats, city_wise_stats] =
        await Promise.all([
          api.getGroupedOrders({
            group_by: "status",
            state: selectedState !== "all" ? selectedState : undefined,
            provider: selectedProvider !== 0 ? selectedProvider : undefined,
            mode: selectedMode !== "all" ? selectedMode : undefined,
            ...(startDate !== "" ? { start_date: startDate } : {}),
            ...(endDate !== "" ? { end_date: endDate } : {}),
            entity: selectedEntity !== 0 ? selectedEntity : undefined,
            user_id: user?.id || "",
            entities: entitiesParam,
          }),
          api.getPharmacyStats({
            state: selectedState !== "all" ? selectedState : undefined,
            mode: selectedMode !== "all" ? selectedMode : undefined,
            ...(startDate !== "" ? { start_date: startDate } : {}),
            ...(endDate !== "" ? { end_date: endDate } : {}),
            user_id: user?.id || "",
            entities: entitiesParam,
          }),
          api.getStateWiseStats({
            state: selectedState !== "all" ? selectedState : undefined,
            provider: selectedProvider !== 0 ? selectedProvider : undefined,
            mode: selectedMode !== "all" ? selectedMode : undefined,
            ...(startDate !== "" ? { start_date: startDate } : {}),
            ...(endDate !== "" ? { end_date: endDate } : {}),
            entity: selectedEntity !== 0 ? selectedEntity : undefined,
            user_id: user?.id || "",
            entities: entitiesParam,
          }),
        ]);

      // Transform pharmacy_stats to ensure all required fields are present
      const transformedPharmacyStats = pharmacy_stats.data.reduce(
        (acc: any, item: any) => {
          if (!acc[item.pharmacy_name]) {
            acc[item.pharmacy_name] = { pharmacy: item.pharmacy_name };
            [...opsStatus, ...trackOrderStatus].forEach(status => {
              acc[item.pharmacy_name][status] = 0;
            });
            acc[item.pharmacy_name].id = item.pharmacy_id;
          }
          acc[item.pharmacy_name][item.status] = item.count;
          return acc;
        },
        {},
      );

      const transformedStateWiseStats = city_wise_stats.data.reduce(
        (acc: any, item: any) => {
          if (!acc[item.state]) {
            acc[item.state] = { state: item.state };
            [...opsStatus, ...trackOrderStatus].forEach(status => {
              acc[item.state][status] = 0;
            });
          }
          // Set the count for the specific status
          acc[item.state][item.status] = item.count;
          return acc;
        },
        {},
      );

      setOrders(orders_data.data);
      setPharmacyStats(transformedPharmacyStats);
      setStateWiseStats(transformedStateWiseStats);
    };

    fetchData();
  }, [
    startDate,
    endDate,
    selectedState,
    selectedProvider,
    selectedMode,
    selectedEntity,
  ]);

  useEffect(() => {
    const down = (e: KeyboardEvent) => {
      if (e.key === "k" && (e.metaKey || e.ctrlKey)) {
        e.preventDefault();
        setIsCommandOpen(open => !open);
      }
    };

    document.addEventListener("keydown", down);
    return () => document.removeEventListener("keydown", down);
  }, []);

  // Fetched entities and generate a dynamic pharmacy commands
  const fetchEntitiesAndCommands = async () => {
    try {
      let data: Entity[] = [];
      const allowedEntityIds = new Set(
          userEntities?.map(e => e.entity_id) ||
          accessLevel?.entities?.map(id => Number(id)) ||
          []
      );

      if (allowedEntityIds.size > 0) {
           // If we have specific allowed entities, use them (or fetch details if needed, but userEntities has names)
           // If userEntities is populated, use it.
           if (userEntities && userEntities.length > 0) {
               data = userEntities.map((e: any) => ({
                   id: e.entity_id,
                   name: e.name,
                   branch_id: e.branch_id || undefined,
                   is_active: true,
                   type: "pharmacy"
               }));
           } else if (allowedEntityIds.size > 0) {
               // Fallback if only accessLevel has IDs but no userEntities details (unlikely if AuthContext is working, but just in case)
               // For security, if we can't get details from userEntities, we shouldn't fetch ALL.
               // We should probably just show IDs or handle it via a specific filtered fetch.
               // For now, let's keep it empty or try to fetch only IDs if we must.
               // But usually user_entity_access is the source of truth now.
               data = [];
           }
      } else {
          // If no allowed IDs, check if Super Admin?
          // If userEntities is empty and accessLevel is empty, assume NO ACCESS or Super Admin?
          // If we assume strict access control, we show NOTHING.
          // Unless we want to support "View All" for super admins who might not have explicit assignments?
          // Current implementation plan implies strict entity assignment.
          // If the user effectively has "All Access" (e.g. explicit wildcard?), we might fetch all.
          // But barring that, show nothing.
           data = [];
      }

      // Generate a dynamic pharmacy commands for the command palette
      const dynamicCommands = generateDynamicPharmacyCommands(data);
      setDynamicPharmacyCommands(dynamicCommands);

      // Store full entities data for any other uses
      setEntities(data);
    } catch (error) {
      console.error("Failed to fetch entities:", error);
      // Clear dynamic commands on error
      setDynamicPharmacyCommands([]);
    }
  };

  useEffect(() => {
    fetchEntitiesAndCommands();
  }, []);

  // Refresh entities when command dialog opens (ensures fresh data)
  useEffect(() => {
    if (isCommandOpen) {
      fetchEntitiesAndCommands();
    }
  }, [isCommandOpen]);

  useEffect(() => {
    const stateParam = searchParams.get("state");
    const providerParam = searchParams.get("provider");
    const statusParam = searchParams.get("status");
    const entityParam = searchParams.get("entity");
    const modeParam = searchParams.get("mode");

    // Always sync all filters with URL (reset to defaults if not present)
    setSelectedState(stateParam || "all");
    setSelectedProvider(providerParam ? Number(providerParam) : 0);
    setStatusFilter(statusParam || "");
    setSelectedEntity(entityParam ? Number(entityParam) : 0);
    setSelectedMode(modeParam || "all");
  }, [searchParams]);

  const updateSearchParams = (key: string, value: string) => {
    const params = new URLSearchParams(searchParams);
    params.set(key, value);
    // Clear status parameter when other filters are changed (except when setting status itself)
    if (key !== "status") {
      params.delete("status");
    }
    navigate(`/?${params.toString()}`);
  };

  const StateWiseStatsTable = () => {
    const orderStatus = [
      ...trackOrderStatus,
      ...opsStatus,
      "Billing",
      "OnHold",
    ].filter((status, index, self) => self.indexOf(status) === index);
    return Object.values(stateWiseStats).length > 0 ? (
      <Table className="w-full">
        <TableHeader>
          <TableRow>
            <TableHead className="px-4">State</TableHead>
            <TableHead className="px-4 text-right">Total</TableHead>
            {orderStatus.map(status => (
              <TableHead key={status} className="px-4 text-right">
                {status}
              </TableHead>
            ))}
          </TableRow>
        </TableHeader>
        <TableBody>
          {Object.values(stateWiseStats).map((stat: any) => (
            <TableRow key={stat.state}>
              <TableCell className="px-4">{stat.state}</TableCell>
              <TableCell className="px-4 text-right">
                <Link
                  to={`/orders?state=${stat.state}&startDate=${
                    startDate || ORDER_START_DATE
                  }&endDate=${
                    endDate || new Date().toISOString().split("T")[0]
                  }`}
                  className="text-blue-600 hover:text-blue-800 dark:text-blue-400 dark:hover:text-blue-300"
                >
                  {orderStatus
                    .map(status => stat[status] || 0)
                    .reduce((a, b) => Number(a) + Number(b), 0)}
                </Link>
              </TableCell>
              {orderStatus.map(status => (
                <TableCell key={status} className="px-4 text-right">
                  <Link
                    to={`/orders?state=${
                      stat.state
                    }&status=${status}&startDate=${
                      startDate || ORDER_START_DATE
                    }&endDate=${
                      endDate || new Date().toISOString().split("T")[0]
                    }`}
                    className="text-blue-600 hover:text-blue-800 dark:text-blue-400 dark:hover:text-blue-300"
                  >
                    {stat[status] || 0}
                  </Link>
                </TableCell>
              ))}
            </TableRow>
          ))}
        </TableBody>
      </Table>
    ) : (
      <p>No data available</p>
    );
  };

  const PharmacyWiseStatsTable = () => {
    const orderStatus = [
      ...trackOrderStatus,
      ...opsStatus,
      "Billing",
      "OnHold",
    ].filter((status, index, self) => self.indexOf(status) === index);
    return (
      <Table className="w-full">
        <TableHeader>
          <TableRow>
            <TableHead className="px-4">Pharmacy</TableHead>
            <TableHead className="px-4">Total</TableHead>
            {orderStatus.map(status => (
              <TableHead key={status} className="px-4">
                {status}
              </TableHead>
            ))}
          </TableRow>
        </TableHeader>
        <TableBody>
          {Object.values(pharmacyStats).map((stat: any) => (
            <TableRow key={stat.pharmacy}>
              <TableCell className="px-4">{stat.pharmacy}</TableCell>
              <TableCell className="px-4 text-right">
                <Link
                  to={`/orders?entity=${stat.id}&startDate=${
                    startDate || ORDER_START_DATE
                  }&endDate=${
                    endDate || new Date().toISOString().split("T")[0]
                  }`}
                  className="text-blue-600 hover:text-blue-800 dark:text-blue-400 dark:hover:text-blue-300"
                >
                  {orderStatus
                    .map(status => stat[status] || 0)
                    .reduce((a, b) => Number(a) + Number(b), 0)}
                </Link>
              </TableCell>
              {orderStatus.map(status => (
                <TableCell key={status} className="px-4">
                  <Link
                    to={`/orders?entity=${stat.id}&status=${status}&startDate=${
                      startDate || ORDER_START_DATE
                    }&endDate=${
                      endDate || new Date().toISOString().split("T")[0]
                    }`}
                    className="text-blue-600 hover:text-blue-800 dark:text-blue-400 dark:hover:text-blue-300"
                  >
                    {stat[status] || 0}
                  </Link>
                </TableCell>
              ))}
            </TableRow>
          ))}
        </TableBody>
      </Table>
    );
  };

  return (
    <div className="min-h-screen bg-white dark:bg-gray-900">
      <div className="p-6">
        <h1 className="text-2xl font-bold mb-4">Dashboard</h1>
        <div className="flex items-center gap-4 mb-6 overflow-x-auto pb-2">
          <TooltipProvider>
            <Tooltip>
              <TooltipTrigger asChild>
                <button
                  className="flex items-center gap-2 px-3 py-2 text-sm hover:bg-gray-100 dark:hover:bg-gray-800 rounded-md dark:text-gray-300 border dark:border-gray-700"
                  type="button"
                  onClick={() => setIsCommandOpen(true)}
                >
                  <Search size={16} />
                  <span className="hidden sm:inline-flex items-center">
                    Search
                    <kbd className="ml-2 pointer-events-none inline-flex h-5 select-none items-center gap-1 rounded border bg-muted px-1.5 font-mono text-[10px] font-medium text-muted-foreground">
                      <span className="text-xs">⌘</span>K
                    </kbd>
                  </span>
                </button>
              </TooltipTrigger>
              <TooltipContent className="dark:bg-gray-800 dark:text-white">
                Search dashboard (⌘K)
              </TooltipContent>
            </Tooltip>
          </TooltipProvider>
          <Filters
            selectedState={selectedState}
            setSelectedState={(state) => {
              setSelectedState(state);
              setStatusFilter(""); // Clear status filter when state changes
            }}
            selectedProvider={Number(selectedProvider)}
            setSelectedProvider={(provider) => {
              setSelectedProvider(provider);
              setStatusFilter(""); // Clear status filter when provider changes
            }}
            selectedMode={selectedMode}
            setSelectedMode={(mode) => {
              setSelectedMode(mode);
              setStatusFilter(""); // Clear status filter when mode changes
            }}
            selectedEntity={selectedEntity}
            setSelectedEntity={(entity) => {
              setSelectedEntity(entity);
              setStatusFilter(""); // Clear status filter when entity changes
            }}
            startDate={startDate}
            setStartDate={(date) => {
              setStartDate(date);
              setStatusFilter(""); // Clear status filter when start date changes
            }}
            endDate={endDate}
            setEndDate={(date) => {
              setEndDate(date);
              setStatusFilter(""); // Clear status filter when end date changes
            }}
            hidePharmacyFilter={accessLevel?.access_level?.provider_id && accessLevel.access_level.provider_id.length > 0}
          />
        </div>

        <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
          <StatusCard
            status="Total Orders"
            count={orders.reduce((a, b) => a + Number(b.count), 0)}
            isSelected={false}
            onClick={() => {}}
          />
          {orders.map(order => (
            <StatusCard
              key={order.status}
              status={order.status}
              count={order.count}
              isSelected={statusFilter === order.status}
              onClick={() => {
                // Clear all other filters when status is selected
                setSelectedState("all");
                setSelectedEntity(0);
                setSelectedProvider(0);
                setSelectedMode("all");
                setStatusFilter(order.status);
                setCurrentPage(1);

                // Update URL to only include status and date filters
                const params = new URLSearchParams();
                params.set("status", order.status);
                if (startDate) params.set("startDate", startDate);
                if (endDate) params.set("endDate", endDate);
                navigate(`/?${params.toString()}`);
              }}
            />
          ))}
        </div>

        <div className="my-4">
          <h2 className="text-xl font-bold">State Wise Stats</h2>
          <StateWiseStatsTable />
        </div>
        {/* Hide Pharmacy Wise Stats - not needed for provider filtering */}
      </div>
      <CommandDialog open={isCommandOpen} onOpenChange={setIsCommandOpen}>
        <CommandInput placeholder="Type a command or search..." />
        <CommandList>
          <CommandEmpty>No results found</CommandEmpty>
          <CommandGroup heading="Quick Access">
            {COMMANDS.map(command => (
              <CommandItem
                asChild
                key={command.id}
                value={command.id}
                onSelect={() => {
                  command.action(navigate);
                  setIsCommandOpen(false);
                }}
                className="cursor-pointer"
              >
                <button
                  className="flex justify-between w-full text-left w-full px-2 py-3 aria-selected:bg-gray-100 dark:aria-selected:bg-gray-700"
                  onKeyDown={(e) => {
                    if (e.key === "Enter") {
                      e.preventDefault();
                      e.stopPropagation();
                      command.action(navigate);
                      setIsCommandOpen(false);
                    }
                  }}
                >
                  <span>{command.name}</span>
                  <span className="text-sm text-gray-500">
                    {command.shortcut.join(" + ")}
                  </span>
                </button>
              </CommandItem>
            ))}
          </CommandGroup>

          <CommandGroup heading="Pharmacy-specific Orders">
            {dynamicPharmacyCommands.map(command => (
              <CommandItem
                asChild
                key={command.id}
                value={command.id}
                onSelect={() => {
                  command.action(navigate);
                  setIsCommandOpen(false);
                }}
                className="cursor-pointer"
              >
                <button className="flex justify-between w-full text-left w-full px-2 py-3 aria-selected:bg-gray-100 dark:aria-selected:bg-gray-700">
                  <span>{command.name}</span>
                  <span className="text-sm text-gray-500">
                    {command.shortcut.join(" + ")}
                  </span>
                </button>
              </CommandItem>
            ))}
          </CommandGroup>

          <CommandGroup heading="States">
            {Object.keys(stateWiseStats).filter(state => state).map(state => (
              <CommandItem
                asChild
                key={state}
                value={state}
                onSelect={() => {
                  window.location.href = `/orders?state=${state}`;
                  setIsCommandOpen(false);
                }}
                className="cursor-pointer"
              >
                <button className="flex justify-between w-full text-left w-full px-2 py-3 aria-selected:bg-gray-100 dark:aria-selected:bg-gray-700">
                  <span>Show {state} Orders</span>
                  <span className="text-sm text-gray-500">
                    {state?.substring(0, 2).toUpperCase() || ""}
                  </span>
                </button>
              </CommandItem>
            ))}
          </CommandGroup>
        </CommandList>
      </CommandDialog>
    </div>
  );
};
